import { omit, orderBy } from "lodash";
import moment from "moment-timezone";

import { memoizeSelector } from "@/redux/helpers/memoizeSelector";
import * as date from "@/utils/date";

import { MODULE } from "./calendar.actionTypes";

const rawUsers = (state) => state.getIn([MODULE, "users"]);
const rawSchedules = (state) => state.getIn([MODULE, "schedules"]);

// `userSelector` calls `.toJS()`, which returns a new object every call - using it directly as a
// memoization input would never hit the cache. Read the underlying Immutable reference (stable
// between unrelated dispatches) and convert once inside the memoized result function instead.
const rawCurrentUser = (state) => state.getIn(["customers", "user"]);
const toJsUser = (user) => (user ? user.toJS() : null);

export const isFetchingUsers = (state) => state.getIn([MODULE, "isFetchingUsers"]);
export const isFetchingSchedules = (state) => state.getIn([MODULE, "isFetchingSchedules"]);
export const isFetchingLabels = (state) => state.getIn([MODULE, "isFetchingLabels"]);
export const isCreatingSchedule = (state) => state.getIn([MODULE, "isCreatingSchedule"]);

export const isCalendarLoading = (state) =>
  isFetchingSchedules(state) || isFetchingLabels(state) || isFetchingUsers(state);

export const getUsers = memoizeSelector([rawUsers, rawCurrentUser], (users, rawUser) => {
  const currentUser = toJsUser(rawUser);
  const formattedUsers = users.map((a) =>
    a.id === currentUser.id ? { ...a, lastName: `${a.lastName} (You)` } : a
  );
  const sortedAssignees = [
    ...(formattedUsers.some((a) => a.id === currentUser.id)
      ? [{ ...formattedUsers.find((a) => a.id === currentUser.id) }]
      : []),
    ...orderBy(
      formattedUsers.filter((a) => a.id !== currentUser.id),
      [(a) => a.firstName.toLowerCase()],
      "asc"
    ),
  ];

  return sortedAssignees;
});

export const getSortedAssignees = memoizeSelector(
  [rawCurrentUser, (_state, assignees) => assignees],
  (rawUser, assignees) => {
    const currentUser = toJsUser(rawUser);
    if (!assignees) {
      return [];
    }

    const formattedAssignees = assignees?.map((a) =>
      a.id === currentUser.id ? { ...a, lastName: `${a.lastName} (You)` } : a
    );
    const sortedAssignees = [
      ...(formattedAssignees?.some((a) => a.id === currentUser.id)
        ? [{ ...formattedAssignees?.find((a) => a.id === currentUser.id) }]
        : []),
      ...orderBy(
        formattedAssignees?.filter((a) => a.id !== currentUser.id),
        [(a) => a.firstName.toLowerCase()],
        "asc"
      ),
    ];

    return sortedAssignees;
  }
);

export const getLabels = (state) => {
  const labels = state.getIn([MODULE, "labels"]);
  return orderBy(labels, [(l) => l.name.toLowerCase()], "asc");
};

export const getSortedAssignedLabels = memoizeSelector([(labels) => labels], (labels) => {
  if (!labels) {
    return [];
  }
  return orderBy(labels, [(l) => l.name.toLowerCase()], "asc");
});

export const getSchedules = (state) => state.getIn([MODULE, "schedules"]).filter((s) => !!s.order);

// Per-row mapper for a raw schedule → the flattened, company-local shape the drawer + event
// tiles read today. Exported so the `@factory/scheduler` adapter can reuse it verbatim for
// `CalendarEvent.meta`, keeping the drawer contract byte-for-byte identical.
export const flattenSchedule = (d, timezone) => {
  const startTime = date.toLocalDateTime({
    date: d.startTime,
    timezone,
    format: "YYYY-MM-DDTHH:mm:ssZ",
  });

  const endTime = date.toLocalDateTime({
    date: d.endTime,
    timezone,
    format: "YYYY-MM-DDTHH:mm:ssZ",
  });

  return {
    ...omit(d, ["order"]),
    ...omit(d.order, ["uid"]),
    orderUid: d?.order?.uid ?? null,
    startTime,
    start: startTime,
    endTime,
    end: endTime,
  };
};

// Keyed off the raw `schedules` slice (a stable reference between unrelated dispatches - see
// useSchedulerData.js), NOT `getSchedules()`'s filtered output, which is a fresh array every call
// and would defeat the memoization.
export const getFormattedSchedules = memoizeSelector([rawSchedules], (schedules) => {
  const timezone = date.getCurrentCompanyTimezone();
  return schedules.filter((s) => !!s.order).map((d) => flattenSchedule(d, timezone));
});

// The drawer opens with a one-time snapshot of the clicked entry. Read the live row back by uid so a
// change made elsewhere (e.g. assignees edited on the order page) shows without closing/reopening.
// Single-entry cache keyed on (schedules, uid): a different uid, or a fresh schedules array, both
// recompute; the same pair returns the cached row.
export const getFormattedScheduleByUid = memoizeSelector(
  [rawSchedules, (_state, uid) => uid],
  (schedules, uid) => {
    if (!uid) {
      return undefined;
    }

    const raw = schedules?.find((s) => s.uid === uid);

    return raw ? flattenSchedule(raw, date.getCurrentCompanyTimezone()) : undefined;
  }
);

export const getSchedulesFilterByUser = (state) =>
  getFormattedSchedules(state).map((s) => ({
    ...s,
    resource: s.user,
    title: `${moment(s.startTime).format("hh:mm A")} ${s.customerName}`,
  }));

export const getFromDate = (state) => state.getIn([MODULE, "fromDate"]);

export const getToDate = (state) => state.getIn([MODULE, "toDate"]);

export const getActiveSchedule = (state) => state.getIn([MODULE, "activeSchedule"]);
export const getContextMenuEntryUid = (state) =>
  state.getIn([MODULE, "contextMenuEntry"])?.uid ?? null;

export const getCalendarView = (state) => state.getIn([MODULE, "calendarView"]);

export const getSelectedResources = (state) => state.getIn([MODULE, "selectedUsers"]);

export const showCustomEventModal = (state) => state.getIn([MODULE, "showCustomEventModal"]);

export const showDeleteCustomEventWarningModal = (state) =>
  state.getIn([MODULE, "showDeleteCustomEventWarningModal"]);

export const showEditCustomEventWarningModal = (state) =>
  state.getIn([MODULE, "showEditCustomEventWarningModal"]);

export const getDeleteScope = (state) => state.getIn([MODULE, "deleteScope"]);

export const getEditScope = (state) => state.getIn([MODULE, "editScope"]);

export const initialCustomEventData = (state) => state.getIn([MODULE, "initialCustomEventData"]);
